import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Typography } from '@mui/material';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../firebase/firebase';
import ScheduifyWordmark from '../brand/ScheduifyWordmark';

// Resolves a business logo's Storage path to a URL, and only reports it once the
// image has loaded so the header never pops in half-drawn.
export const useBusinessLogoUrl = (logoImagePath?: string | null): string | null => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoImagePath) {
      setLogoUrl(null);
      return;
    }
    let active = true;
    getDownloadURL(ref(storage, logoImagePath))
      .then((url) => {
        const img = new Image();
        img.onload = () => {
          if (active) setLogoUrl(url);
        };
        img.src = url;
      })
      .catch((error) => console.error('Failed to load logo image:', error));
    return () => {
      active = false;
    };
  }, [logoImagePath]);

  return logoUrl;
};

interface AuthShellProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  logoUrl?: string | null;
  businessName?: string;
  maxWidth?: 'sm' | 'md';
  children: React.ReactNode;
}

// The frame shared by login, sign-up and password reset: brand blue backdrop,
// one white card, Scheduify wordmark and the business's own logo on top.
const AuthShell: React.FC<AuthShellProps> = ({
  title,
  subtitle,
  logoUrl,
  businessName,
  maxWidth = 'sm',
  children,
}) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      py: { xs: 2, sm: 6 },
      px: 2,
      bgcolor: '#1976d2',
      backgroundImage: 'radial-gradient(circle at 15% 0%, rgba(255,255,255,.16), transparent 45%), radial-gradient(circle at 100% 100%, rgba(13,71,161,.55), transparent 55%)',
    }}
  >
    <Container maxWidth={maxWidth} disableGutters>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'rgba(25,118,210,.18)',
          boxShadow: '0 24px 60px rgba(13,71,161,.28)',
        }}
      >
        <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={businessName ? `${businessName} logo` : 'Business logo'}
              sx={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain', mb: 1 }}
            />
          ) : null}
          <ScheduifyWordmark variant={logoUrl ? 'h5' : 'h3'} align="center" />
          <Typography variant="h4" component="h1" align="center" fontWeight={850}>{title}</Typography>
          {subtitle ? <Typography color="text.secondary" align="center">{subtitle}</Typography> : null}
        </Stack>
        {children}
      </Paper>
    </Container>
  </Box>
);

export default AuthShell;
